import React from 'react';
import {
  FaProjectDiagram,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaTasks,
  FaCheckDouble,
} from 'react-icons/fa';
import { Card, SectionTitle, Badge, Spinner, EmptyState } from './Primitives';
import { planTaskGenMeta, REGENERATION_MODE } from '../utils/constants';

function CoverageStat({ label, value, tone = 'default' }) {
  const tones = {
    default: 'text-gray-800 dark:text-gray-100',
    ok: 'text-emerald-600 dark:text-emerald-400',
    warn: 'text-amber-600 dark:text-amber-400',
  };
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-center dark:border-gray-700 dark:bg-gray-900/50">
      <div className={`text-lg font-semibold tabular-nums ${tones[tone]}`}>{value}</div>
      <div className="text-[11px] text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );
}

function TaskRow({ task, regenerationMode }) {
  const meta = planTaskGenMeta(task.generation?.status);
  const willExecute = task.willExecute !== false;
  const forced =
    regenerationMode === REGENERATION_MODE.ALL &&
    task.generation?.status === 'UP_TO_DATE' &&
    willExecute;

  return (
    <tr className={willExecute ? '' : 'opacity-55'}>
      <td className="px-3 py-2 font-mono text-gray-500 dark:text-gray-400">{task.id}</td>
      <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{task.table?.name}</td>
      <td
        className="max-w-[14rem] truncate px-3 py-2 text-gray-500 dark:text-gray-400"
        title={task.skeleton?.path}
      >
        {task.skeleton?.path?.split('/').pop()}
      </td>
      <td className="px-3 py-2">
        {meta ? (
          <Badge className={meta.style}>{meta.label}</Badge>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        {willExecute ? (
          forced ? (
            <Badge className="bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/50 dark:text-fuchsia-300">
              Rigenerazione forzata
            </Badge>
          ) : (
            <Badge className="bg-primary/10 text-primary dark:bg-primary/20">Da eseguire</Badge>
          )
        ) : (
          <Badge className="bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
            Saltato
          </Badge>
        )}
      </td>
    </tr>
  );
}

/**
 * Step 4: revisione del piano. Copertura tabella × scheletro, scheletri
 * compatibili/incompatibili, errori bloccanti e matrice dei task con lo stato
 * di copertura (distinto da task.status).
 */
export default function PlanReview({ plan, blockingErrors, error, loading, regenerationMode }) {
  if (loading) {
    return (
      <Card className="p-5">
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
          <Spinner className="h-4 w-4" /> Calcolo del piano…
        </div>
      </Card>
    );
  }

  if (!plan && !error && (!blockingErrors || blockingErrors.length === 0)) {
    return (
      <Card className="p-5">
        <SectionTitle icon={FaProjectDiagram} title="Piano" subtitle="Non ancora calcolato" />
        <div className="mt-4">
          <EmptyState
            icon={FaProjectDiagram}
            title="Nessun piano"
            hint="Seleziona macrofunzioni e scheletri, poi calcola il piano dal riepilogo."
          />
        </div>
      </Card>
    );
  }

  const compatible = plan?.skeletons?.compatible || [];
  const incompatible = plan?.skeletons?.incompatible || [];
  const tasks = plan?.tasks || [];
  const hasBlocking = Array.isArray(blockingErrors) && blockingErrors.length > 0;
  const allUpToDate =
    plan && !hasBlocking && (plan.expectedExecutions ?? 0) === 0 && (plan.totalCombinations ?? 0) > 0;

  return (
    <Card className="p-5">
      <SectionTitle
        icon={FaProjectDiagram}
        title="Piano di generazione"
        subtitle={`${plan?.expectedExecutions ?? tasks.length} esecuzioni previste`}
      />

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
          <FaExclamationTriangle size={14} className="mt-0.5" /> {error}
        </div>
      )}

      {/* Copertura tabella × scheletro */}
      {plan && (
        <div className="mt-4 grid grid-cols-3 gap-2">
          <CoverageStat label="Combinazioni" value={plan.totalCombinations ?? tasks.length} />
          <CoverageStat
            label="Da eseguire"
            value={plan.expectedExecutions ?? 0}
            tone={(plan.expectedExecutions ?? 0) > 0 ? 'default' : 'ok'}
          />
          <CoverageStat
            label="Già aggiornate"
            value={plan.skippedUpToDate ?? 0}
            tone={(plan.skippedUpToDate ?? 0) > 0 ? 'ok' : 'default'}
          />
        </div>
      )}

      {/* Stato positivo: nulla da rigenerare */}
      {allUpToDate && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-900/20 dark:text-emerald-300">
          <FaCheckDouble size={14} className="mt-0.5" />
          <span>
            Tutto aggiornato per gli scheletri selezionati. Nessuna esecuzione
            necessaria; puoi forzare una rigenerazione completa dal riepilogo.
          </span>
        </div>
      )}

      {/* Errori bloccanti */}
      {hasBlocking && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-800/60 dark:bg-amber-900/20">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-300">
            <FaExclamationTriangle size={13} /> Errori bloccanti
          </div>
          <ul className="space-y-1.5">
            {blockingErrors.map((be, i) => (
              <li key={i} className="text-xs text-amber-700 dark:text-amber-300">
                <span className="font-semibold">{be.code}</span>
                {be.path ? ` · ${be.path}` : ''}
                {be.message ? ` — ${be.message}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Scheletri */}
      {(compatible.length > 0 || incompatible.length > 0) && (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-emerald-600 dark:text-emerald-400">
              <FaCheckCircle size={13} /> Compatibili ({compatible.length})
            </div>
            <ul className="space-y-1.5">
              {compatible.map((s) => (
                <li
                  key={`${s.root}::${s.path}`}
                  className="truncate rounded-lg bg-emerald-50 px-3 py-1.5 text-xs text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
                  title={`${s.root} / ${s.path}`}
                >
                  {s.path.split('/').pop()}
                </li>
              ))}
              {compatible.length === 0 && <li className="text-xs text-gray-400">Nessuno</li>}
            </ul>
          </div>
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-red-600 dark:text-red-400">
              <FaTimesCircle size={13} /> Incompatibili ({incompatible.length})
            </div>
            <ul className="space-y-1.5">
              {incompatible.map((s) => (
                <li
                  key={`${s.root}::${s.path}`}
                  className="rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300"
                  title={s.reason || ''}
                >
                  <div className="truncate font-medium">{s.path.split('/').pop()}</div>
                  {s.reason && <div className="mt-0.5 opacity-80">{s.reason}</div>}
                </li>
              ))}
              {incompatible.length === 0 && <li className="text-xs text-gray-400">Nessuno</li>}
            </ul>
          </div>
        </div>
      )}

      {/* Matrice task con stato di copertura */}
      {tasks.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
            <FaTasks size={13} className="text-primary" /> Task ({tasks.length})
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
            <table className="w-full min-w-[36rem] text-left text-xs">
              <thead className="bg-gray-50 text-gray-500 dark:bg-gray-900/50 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 font-medium">Task</th>
                  <th className="px-3 py-2 font-medium">Tabella</th>
                  <th className="px-3 py-2 font-medium">Scheletro</th>
                  <th className="px-3 py-2 font-medium">Copertura</th>
                  <th className="px-3 py-2 font-medium">Esecuzione</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {tasks.map((t) => (
                  <TaskRow key={t.id} task={t} regenerationMode={regenerationMode} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}
